import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { AppBar, Tabs, Tab, Chip } from '@mui/material';
import { withTheme } from 'styled-components';
import { themePropType } from '@themes';
import { ToolTip } from '@custom-components';
import { flowRight as compose } from 'lodash';
import { PortalButton } from '../PortalButton/PortalButton';

import {
  TabBarContainer,
  TabContainer,
  SubBar,
  StyledCenteredContainer,
  LeftSubBarContainer,
  RightSubBarContainer,
  ChipBar
} from './TabBar.styles';
import { SearchBar } from '../SearchBar/SearchBar';
import { ReactChildrenPropType } from '../../../lib/commonPropTypes';

class TabBar extends Component {
  handleChange = (event, tabValue) => {
    const { handleTabChange } = this.props;
    handleTabChange(tabValue);
  };

  static getSubBarContents = (item, index) => {
    const { type, props } = item;
    const {
      className,
      disabled,
      href,
      name,
      onClick,
      target,
      btnTheme,
      iconTheme,
      uppercase,
      icon,
      loading,
      label,
      fullWidth,
      reqPermission,
      type: buttonType,
      submitSearch,
      value,
      onChange,
      'data-testid': testId
    } = props ?? {};
    if (type === 'tabBarButton') {
      const key = `button${index}`;

      return (
        <PortalButton
          key={key}
          className={className}
          disabled={disabled}
          href={href}
          name={name}
          onClick={onClick}
          target={target}
          btnTheme={btnTheme}
          iconTheme={iconTheme}
          type={buttonType}
          uppercase={uppercase}
          icon={icon}
          loading={loading}
          label={label}
          fullWidth={fullWidth}
          reqPermission={reqPermission}
          data-testid={testId}
        />
      );
    }
    if (type === 'label') {
      const key = `label${index}`;
      return <div key={key}>{label}</div>;
    }
    const key = `search${index}`;
    return (
      <SearchBar
        key={key}
        submitSearch={submitSearch}
        placeholder={label}
        name={name}
        value={value}
        onChange={onChange}
        type={buttonType}
      />
    );
  };

  static renderTab = (label, count, disabled, chipStyle, index, key) => (
    <Tab
      icon={
        <StyledCenteredContainer>
          {label} {count && count > 0 ? <Chip style={chipStyle} label={count} /> : ''}
        </StyledCenteredContainer>
      }
      disabled={disabled}
      value={index}
      key={key}
    />
  );

  render() {
    const {
      tabs,
      tabValue,
      noTabs,
      subBar,
      subBarTheme,
      subBarThemeNeutral,
      customThemeStyle,
      scrollButtons,
      leftSubBarContents,
      rightSubBarContents,
      advancedSearch,
      chips,
      onDeleteChip,
      theme = {}
    } = this.props;
    const barChipStyle = {
      backgroundColor: theme.midTheme,
      color: theme.lightText,
      margin: '5px'
    };
    const chipStyle = {
      backgroundColor: theme.midTheme,
      color: theme.lightText,
      height: '20px',
      marginLeft: '5px',
      cursor: 'pointer'
    };

    return (
      <TabBarContainer themeCustom={customThemeStyle}>
        {tabs.length !== 0 && !noTabs && (
          <AppBar
            style={{ boxShadow: 'none', color: theme.lighterText, backgroundColor: theme.darkTheme }}
            position="static"
            enableColorOnDark
          >
            <Tabs
              indicatorColor="primary"
              textColor="inherit"
              value={tabValue}
              onChange={this.handleChange}
              variant="scrollable"
              scrollButtons={scrollButtons}
              TabIndicatorProps={{ style: { height: '5px', backgroundColor: theme.highlightTheme } }}
            >
              {tabs.map(
                ({ label, count, disabled = false, disabledMessage = 'This tab is currently unavailable' }, index) => {
                  const key = `${index}tab`;

                  if (disabled) {
                    return (
                      <ToolTip content={disabledMessage} key={key}>
                        {TabBar.renderTab(label, count, disabled, chipStyle, index, key)}
                      </ToolTip>
                    );
                  }

                  return TabBar.renderTab(label, count, disabled, chipStyle, index, key);
                }
              )}
            </Tabs>
          </AppBar>
        )}
        {subBar && (
          <SubBar className={subBarTheme} themeNeutral={subBarThemeNeutral} themeCustom={customThemeStyle}>
            {leftSubBarContents.length > 0 && (
              <LeftSubBarContainer>{leftSubBarContents.map(TabBar.getSubBarContents)}</LeftSubBarContainer>
            )}
            {rightSubBarContents.length > 0 && (
              <RightSubBarContainer>{rightSubBarContents.map(TabBar.getSubBarContents)}</RightSubBarContainer>
            )}
          </SubBar>
        )}
        {advancedSearch}
        {chips.length !== 0 && (
          <ChipBar>
            {chips.map((chip, index) => (
              <Chip
                key={`chip-${String(index)}`}
                style={barChipStyle}
                name={chip.name}
                label={chip.value}
                onDelete={() => onDeleteChip(chip.name)}
              />
            ))}
          </ChipBar>
        )}
        {tabs.map((tab, index) => {
          const key = `${index}tabContainer`;
          return (
            tabValue === index && (
              <TabContainer className={subBar ? 'subBar' : ''} key={key} themeCustom={customThemeStyle}>
                {tab.children}
              </TabContainer>
            )
          );
        })}
      </TabBarContainer>
    );
  }
}

TabBar.propTypes = {
  theme: themePropType,
  /** Page component to wrap with this layout. */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      children: ReactChildrenPropType,
      disabled: PropTypes.bool,
      disabledMessage: PropTypes.string
    })
  ),
  tabValue: PropTypes.number,
  noTabs: PropTypes.bool,
  subBar: PropTypes.bool,
  subBarTheme: PropTypes.string,
  subBarThemeNeutral: PropTypes.string,
  customThemeStyle: PropTypes.objectOf(PropTypes.shape({})),
  scrollButtons: PropTypes.string,
  leftSubBarContents: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['tabBarButton', 'searchBar', 'label']),
      props: PropTypes.shape({
        'data-testid': PropTypes.string
      })
    })
  ),
  rightSubBarContents: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['tabBarButton', 'searchBar', 'label']),
      props: PropTypes.shape({
        'data-testid': PropTypes.string
      })
    })
  ),
  handleTabChange: PropTypes.func,
  advancedSearch: PropTypes.node,
  chips: PropTypes.arrayOf(PropTypes.shape({})),
  onDeleteChip: PropTypes.func
};

TabBar.defaultProps = {
  tabs: [],
  noTabs: false,
  subBar: false,
  subBarTheme: '',
  subBarThemeNeutral: '',
  customThemeStyle: {},
  scrollButtons: 'auto',
  leftSubBarContents: [],
  rightSubBarContents: [],
  tabValue: 0,
  handleTabChange: () => {},
  advancedSearch: null,
  chips: [],
  onDeleteChip: () => {},
  theme: {}
};

const ComposedTabBar = compose(withTheme)(TabBar);
export default ComposedTabBar;
